import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { IPercentageSplit } from '../../../entities/offsiteCampaign';
import DateTimePicker from 'sections/campaigns/ui/components/DateTimePicker';
import { SplitTestListItem } from './SplitTestListItem';
import {
  Button,
  ISelectMiniOption,
  Input,
  Modal,
  SvgIcon,
  TooltipComponent,
  Alert,
  Label,
  useTheme,
} from '@salecycle/ui-component-library';
import { uuid } from 'utils';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../context/UseOffsiteCampaignEditorContext';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UpdatedSplitTestPercentages } from '../common/PercentageCalculator';
import { FC, useState } from 'react';
import {
  isSplitTestEndDateSelected,
  isSplitTestStartDateSelected,
  segmentNameValidation,
  splitTestNameValidation,
} from '../progress';
import React from 'react';
import { useFeatureFlags } from 'core/app/AppContext';
import moment from 'moment';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';

const List = styled.ul`
  color: ${({ theme }) => theme.vars.newColor.darkGS2};
  display: flex;
  width: '100%';
`;

const Item = styled.li`
  border: 1px solid black;
  list-style-type: none;
  margin: 2px;
  display: inline-block;
`;

const ModalDivRow = styled.div`
  display: flex;
  flex-direction: row;
  min-width: '100%';
`;

interface IProps {
  splits: IPercentageSplit[];
  segmentId: string;
  campaignState: CampaignState;
  newCycleOptions: ISelectMiniOption[];
  sessionTime: number;
}
export const SplitTestList: FC<IProps> = props => {
  const theme = useTheme();
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { campaign } = useOffsiteCampaignEditorState();
  const { t } = useTranslation('campaigns');
  const [charCode, setCharCodeValue] = useState<number>(65);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { flagEnabled } = useFeatureFlags();
  const [splitToClone, setSplitToClone] = useState<string>(null);
  const featureSplitTest = flagEnabled('ld-split-test-scheduling');
  const { timezone } = usePreferencesState();

  const [startDate, setStartDate] = useState<moment.Moment>(
    campaign.splitTestStartDate ? moment(campaign.splitTestStartDate).tz(timezone) : null,
  );
  const [endDate, setEndDate] = useState<moment.Moment>(
    campaign.splitTestEndDate ? moment(campaign.splitTestEndDate).tz(timezone) : null,
  );
  const [splitTestName, setSplitTestName] = useState<string>('');
  const [splitTestNameNew, setNewSplitTestName] = useState<string>();
  React.useEffect(() => {
    const segment = campaign.segments?.find(s => s.id === props.segmentId);

    if (segment && featureSplitTest) {
      setNewSplitTestName(segment?.splitTestName);
    }
  }, [props, featureSplitTest, campaign.segments]);

  const getCharCodeValue = (): number => {
    const updatedCharCode = charCode + 1;
    setCharCodeValue(updatedCharCode);
    return updatedCharCode;
  };

  const resetCharCodeValue = () => setCharCodeValue(65);

  const isNewSplitTestNameValid = featureSplitTest
    ? splitTestNameValidation(splitTestNameNew, t).expression
    : undefined;

  const isSplitTestNameValid = segmentNameValidation(splitTestName, t).expression;

  const handleClose = () => {
    setSplitToClone(null);
    setIsOpen(false);
  };

  const isStartDateSelected = featureSplitTest ? isSplitTestStartDateSelected(startDate, t).expression : undefined;
  const isEndDateSelected = featureSplitTest ? isSplitTestEndDateSelected(endDate, t).expression : undefined;

  const addSplitTestOnClick = (splitId?: string) => {
    setSplitToClone(splitId);
    if (props.splits.length === 1) {
      setIsOpen(true);
    } else {
      addSplitTest();
    }
  };

  const updateSplitTests = (segmentId: string, updatedSplits: IPercentageSplit[]) => {
    dispatch({
      type: 'UPDATE_SPLIT_TESTS',
      payload: {
        segmentId: segmentId,
        updatedPercentageSplits: UpdatedSplitTestPercentages(updatedSplits),
      },
    });
  };

  // Use this when we want to preserve user-entered percentages exactly as provided
  const updateSplitTestsRaw = (segmentId: string, updatedSplits: IPercentageSplit[]) => {
    dispatch({
      type: 'UPDATE_SPLIT_TESTS',
      payload: {
        segmentId: segmentId,
        updatedPercentageSplits: updatedSplits,
      },
    });
  };

  const setSplitTestDates = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    dispatch({
      type: 'SET_CAMPAIGN_SPLIT_TEST_START_TIME',
      payload: { splitTestStartDate: startDate && startDate.valueOf() },
    });
    dispatch({
      type: 'SET_CAMPAIGN_SPLIT_TEST_END_TIME',
      payload: { splitTestEndDate: endDate && endDate.valueOf() },
    });
  };

  const updateSplitTestId = () => {
    dispatch({
      type: 'UPDATE_SPLIT_TEST_ID',
      payload: {
        segmentId: props.segmentId,
        splitTestId: uuid(),
      },
    });
  };

  const addSplitTest = (updateSegmentName = false) => {
    const newPercentageSplit: IPercentageSplit = {
      id: uuid(),
      percentage: 0,
      cycles: splitToClone ? props.splits.find(s => s.id === splitToClone).cycles : [],
      name: `Variant ${String.fromCharCode(getCharCodeValue())}`,
    };

    const updatedSplits = [...props.splits, newPercentageSplit];

    if (updatedSplits.length === 2) {
      updatedSplits[0].name = 'Variant A';
    }

    updateSplitTests(props.segmentId, updatedSplits);
    featureSplitTest && updateSplitTestId();
    if (updateSegmentName && !featureSplitTest) {
      dispatch({
        type: 'UPDATE_SEGMENT_NAME',
        payload: {
          segmentId: props.segmentId,
          name: splitTestName,
        },
      });
    } else if (updateSegmentName && featureSplitTest) {
      dispatch({
        type: 'UPDATE_SPLIT_TEST_NAME',
        payload: {
          segmentId: props.segmentId,
          name: splitTestNameNew,
        },
      });
    }
    setSplitTestDates(startDate, endDate);

    setSplitToClone(null);
    setIsOpen(false);
  };

  const deleteSplitTest = (splitId: string) => {
    const updatedSplits = props.splits.filter(s => s.id !== splitId);

    if (updatedSplits.length === 1) {
      // This is the last segment left - remove the split test ID as we're not in a split test anymore
      updatedSplits[0].name = 'Default';
      resetCharCodeValue();

      if (featureSplitTest) {
        dispatch({
          type: 'UPDATE_SPLIT_TEST_ID',
          payload: {
            segmentId: props.segmentId,
            splitTestId: undefined,
          },
        });
      }
    } else {
      // We have other segments, keep the split test ID
      if (featureSplitTest) {
        dispatch({
          type: 'UPDATE_SPLIT_TEST_ID',
          payload: {
            segmentId: props.segmentId,
            splitTestId: uuid(),
          },
        });
      }
    }

    updateSplitTests(props.segmentId, updatedSplits);
  };

  const handlePercentageChange = (splitId: string, newPercentage: number) => {
    if (props.splits.length === 2) {
      const updatedSplits = props.splits.map(split => {
        if (split.id === splitId) {
          return { ...split, percentage: newPercentage };
        } else {
          return { ...split, percentage: 100 - newPercentage };
        }
      });
      updateSplitTestsRaw(props.segmentId, updatedSplits);
    } else {
      const updatedSplits = props.splits.map(split =>
        split.id === splitId ? { ...split, percentage: newPercentage } : split,
      );
      updateSplitTestsRaw(props.segmentId, updatedSplits);
    }
  };

  return (
    <>
      <Modal
        heading={
          featureSplitTest
            ? t('campaigns:offsiteEditor.segments.splitTests.modal.newHeading')
            : t('campaigns:offsiteEditor.segments.splitTests.modal.heading')
        }
        isOpen={isOpen}
        onCloseButtonClicked={handleClose}
        minHeight={250}
        onOkButtonClicked={() => addSplitTest(true)}
        okButtonDisabled={
          featureSplitTest
            ? !isNewSplitTestNameValid || !isStartDateSelected || !isEndDateSelected
            : !isSplitTestNameValid
        }
        okButtonLabel={t('campaigns:offsiteEditor.segments.splitTests.modal.saveButton')}
        variation="clean"
        overflow="visible"
        minWidth={500}
      >
        {!featureSplitTest && (
          <>
            {!featureSplitTest && <p>{t('campaigns:offsiteEditor.segments.splitTests.modal.label')}</p>}
            <Input
              label={t('campaigns:offsiteEditor.segments.splitTests.modal.segmentName')}
              ariaLabel={t('campaigns:offsiteEditor.segments.splitTests.modal.segmentName')}
              value={splitTestName}
              onChange={setSplitTestName}
              placeholder={t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestNamePlaceholder')}
              validationMessage={
                splitTestName && !isSplitTestNameValid && segmentNameValidation(splitTestName, t).message
              }
              validationState={isSplitTestNameValid ? 'complete' : splitTestName ? 'warning' : null}
            />
          </>
        )}

        {featureSplitTest && (
          <ModalDivRow>
            <Input
              inputTestId="modal-splittest-name"
              label={`${t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestNameLabel')}`}
              ariaLabel={`${t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestNameLabel')}`}
              value={splitTestNameNew}
              onChange={setNewSplitTestName}
              minWidth="250px"
              required
              placeholder={t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestNameLabel')}
              validationMessage={
                splitTestNameNew && !isNewSplitTestNameValid && splitTestNameValidation(splitTestNameNew, t).message
              }
              validationState={isNewSplitTestNameValid ? 'complete' : splitTestNameNew ? 'warning' : null}
              tooltipComponent={
                <>
                  <TooltipComponent
                    id="splitTestNameToolTip"
                    position="TopRight"
                    label={t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestNameTooltip')}
                  >
                    <SvgIcon
                      id="circle-question"
                      name="circle-question"
                      size="16"
                      color={theme.designToken.primary700}
                    />
                  </TooltipComponent>
                </>
              }
            />
          </ModalDivRow>
        )}

        {featureSplitTest && (
          <>
            <ModalDivRow>
              <Label
                name={t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestDatesLabel')}
                marginBottom={true}
              ></Label>
            </ModalDivRow>
            <ModalDivRow>
              <DateTimePicker
                label={t('campaigns:dates.start.label')}
                time={startDate}
                onChange={newDate => setStartDate(newDate)}
                dateAriaLabel={t('campaigns:dates.start.dateLabel')}
                datePlaceholder={t('campaigns:offsiteEditor.segments.splitTests.modal.selectDate')}
                timeAriaLabel={t('campaigns:dates.start.timeLabel')}
                focusedDate={false}
                timezone="GMT"
              />
              <TooltipComponent
                id="splitTestDateToolTip"
                position="TopRight"
                label={t('campaigns:offsiteEditor.segments.splitTests.modal.splitTestDateTooltip')}
              >
                <SvgIcon id="circle-question" name="circle-question" size="16" color={theme.designToken.primary700} />
              </TooltipComponent>
            </ModalDivRow>
            <ModalDivRow>
              <DateTimePicker
                label={t('campaigns:dates.end.label')}
                time={endDate}
                onChange={newDate => setEndDate(newDate)}
                dateAriaLabel={t('campaigns:dates.end.dateLabel')}
                datePlaceholder={t('campaigns:offsiteEditor.segments.splitTests.modal.selectDate')}
                timeAriaLabel={t('campaigns:dates.end.timeLabel')}
                focusedDate={false}
                timezone="GMT"
              />
            </ModalDivRow>
            <br />
            <ModalDivRow>
              <Alert heading={t('campaigns:offsiteEditor.segments.splitTests.modal.alertDescription')} variant="info" />
            </ModalDivRow>
          </>
        )}
      </Modal>
      <List data-testid="SplitTestList">
        {props.splits.map(split => (
          <Item key={`${split.id}`}>
            <SplitTestListItem
              key={`${props.segmentId}-${split.id}`}
              split={split}
              segmentId={props.segmentId}
              campaignState={props.campaignState}
              newCycleOptions={props.newCycleOptions}
              sessionTime={props.sessionTime}
              splitsLength={props.splits.length}
              cloneSplitTest={addSplitTestOnClick}
              deleteSplitTest={deleteSplitTest}
              splits={props.splits}
              onPercentageChange={handlePercentageChange}
            />
          </Item>
        ))}
      </List>
      <Button data-testid="modal-save-button" variation="secondary" type="submit" onClick={() => addSplitTestOnClick()}>
        {t('campaigns:offsiteEditor.segments.splitTests.addNewSplitTest.label')}
      </Button>
    </>
  );
};
export default SplitTestList;
